import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

import { API_CONFIG } from '../config/api.config';
import { AuthService } from '../services/auth.service';

const ADMIN_API_PREFIX = `${API_CONFIG.baseUrl}/admin/`;

/**
 * Attaches the Bearer token to every request against API_CONFIG.baseUrl.
 *
 * - 401 → the session is terminal (no refresh token on the backend): clear it
 *   and redirect to /login.
 * - 403 on an /admin/* endpoint → the caller is authenticated but not (or no
 *   longer) an admin. Do NOT touch the session — just send them to /dashboard.
 *   Scoped to /admin/* on purpose: several product endpoints legitimately answer
 *   403 by tier (e.g. FREE hitting cv-rewrite/reality-check POSTs) and those must
 *   keep flowing to the component's own error handling untouched.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!req.url.startsWith(API_CONFIG.baseUrl)) {
    return next(req);
  }

  const token = authService.auth()?.accessToken;
  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) {
          authService.handleUnauthorized();
        } else if (error.status === 403 && authReq.url.startsWith(ADMIN_API_PREFIX)) {
          void router.navigateByUrl('/dashboard');
        }
      }
      return throwError(() => error);
    }),
  );
};
